/**
 *
 *  With Booking Time
 *  HOC Component
 *
 *  Handles changing booking time, provide with min/max times and time slots
 *  Initialises booking time from start_time query string.
 *
 *  Created by Vladimir Shishlyannikov on 21/03/2023.
 *  Copyright (c) 2023-present iWoo Ltd. All rights reserved.
 *
 */

import React from "react";
import PropTypes from "prop-types"; // use directly
import hoistNonReactStatic from "hoist-non-react-statics";
import { withRouter } from "react-router";
import moment from "moment-timezone";

// Utils
import * as _ from "../../utils/_";
import * as dates from "../../utils/dates";
import {
  inRangeTime,
  getMomentWithTz,
  isDateInDayRange,
} from "../../utils/date";

// ignoreStartTimeQueryParam - controls whether the WrappedComponent depends on
// start_time query param or it should not rely on it
// Potential bug: when one component depends setting date on this param,
// while the others don't
// Priority of setting booking time:
// 1. Query string
// 2. bookingTime prop
// 3. new Date()
export const withBookingTime = (ignoreStartTimeQueryParam) => {
  // HOC Factory
  function wrapComponent(WrappedComponent) {
    var displayName =
      WrappedComponent.displayName || WrappedComponent.name || "Component";

    class HOCComponent extends React.Component {
      static displayName = `WithBookingTime(${displayName})`;

      static propTypes = {
        bookingTime: PropTypes.instanceOf(Date),
        partnerAvailability: PropTypes.array,
      };

      constructor(props) {
        super(props);

        this.minTime = getMomentWithTz(new Date(), props.region)
          .startOf("day")
          .add(330, "minute");
        this.maxTime = this.minTime
          .clone()
          .endOf("month")
          .add(2, "M")
          .endOf("month")
          .hour(23)
          .minutes(30)
          .seconds(0);

        const { bookingTime, partnerAvailability, confirmedBooking } = props;
        var timeSlots,
          availableDays = [];

        var query = new URLSearchParams(props.location.search);
        var startTime = Number(query.get("start_time"));

        this.bookingTime = bookingTime
          ? getMomentWithTz(bookingTime, props.region)
          : startTime && !ignoreStartTimeQueryParam
          ? getMomentWithTz(startTime, props.region)
          : getMomentWithTz(new Date(), props.region);

        var _startTimes =
          partnerAvailability && partnerAvailability.length
            ? partnerAvailability
            : confirmedBooking &&
              confirmedBooking.partner &&
              confirmedBooking.partner.availability;

        if (_startTimes) {
          availableDays = this.getAvailableDays(_startTimes);

          let _startTime = startTime ? moment(startTime) : undefined;

          if (
            !startTime ||
            !this.isTimeAvailable(availableDays, _startTime) ||
            (startTime &&
              this.isTimeAvailable(availableDays, _startTime) &&
              !ignoreStartTimeQueryParam)
          ) {
            this.bookingTime = this.nextAvailableDate(
              availableDays,
              _startTime && _startTime.isValid() ? _startTime : undefined,
              true
            );
          }

          timeSlots = this.getTimeSlots(partnerAvailability);
        }

        this.changeBookingTime = this.changeBookingTime.bind(this);

        this.state = {
          availableDays,
          timeSlots,
          bookingTime: this.bookingTime.toDate(),
        };
      }

      componentDidUpdate(prevProps) {
        const { partnerAvailability } = this.props;

        var timeSlots,
          availableDays = [];

        if (
          !_.isShallowEqual(prevProps.partnerAvailability, partnerAvailability)
        ) {
          availableDays = this.getAvailableDays(partnerAvailability); //Object.keys(nextProps.partnerAvailability);

          var query = new URLSearchParams(this.props.location.search);
          var startTime = Number(query.get("start_time"));

          // We can't just set next available time, as in Checkout it'll
          // default time to the next available. We check for start_time
          // query string is not present to not reset the time

          // REMOVE THIS COMMENT LATER:
          // BUG!!
          // This check will control setting the date up in the Time Slots Pop-up
          // In situations when there's start_time in the URL by any reason
          // it'll skip setting the date in Day/ Time Picker on the Partner List page
          // The last check this.bookingTime.isValid() is messing with Checkout page
          var _startTime = startTime
            ? getMomentWithTz(startTime, this.props.region)
            : undefined;

          if (!_startTime && this.props.bookingTime) {
            _startTime = getMomentWithTz(
              this.props.bookingTime,
              this.props.region
            );
          }

          if (
            !this.isTimeAvailable(availableDays, _startTime) &&
            !ignoreStartTimeQueryParam
          ) {
            this.bookingTime = this.nextAvailableDate(
              availableDays,
              _startTime && _startTime.isValid() ? _startTime : undefined
            );

            // So we can go to Checkout. Probably here we re-set the booking time
            this.props.onDateTimeSelect(this.bookingTime.toDate(), true);
          }

          // Is it okay to set time slots after we get nextAvailableDate??
          timeSlots = this.getTimeSlots(partnerAvailability);

          this.setState({
            availableDays,
            timeSlots,
          });
        }

        if (prevProps.bookingTime !== this.props.bookingTime) {
          this.bookingTime = this.props.bookingTime
            ? moment(this.props.bookingTime)
            : null;
        }

        // Clearing the booking time
        if (prevProps.bookingTime && this.props.bookingTime === null) {
          this.bookingTime = null; // .clear();
          this.forceUpdate();
        }
      }

      render() {
        return React.createElement(WrappedComponent, {
          ...this.props,
          value: this.bookingTime && this.bookingTime.toDate(),
          min: this.minTime.toDate(),
          max: this.maxTime.toDate(),
          availableDays: Object.keys(this.state.availableDays),
          timeSlots: this.state.timeSlots,
          // nextAvailableDate: this.nextAvailableDate,
          onDateTimeSelect: this.changeBookingTime,
        });
      }

      // TODO: decide later how to pass culture locale
      /**
       * @param value
       * @param args
       */
      changeBookingTime(value, ...args) {
        const { partnerAvailability } = this.props;
        const newDate = moment(value);

        if (value && newDate.isValid()) {
          let timeSlots;

          timeSlots = this.getTimeSlots(partnerAvailability, newDate);

          if (
            inRangeTime(
              value,
              this.minTime.toDate(),
              this.maxTime.toDate(),
              timeSlots
            )
          ) {
            this.bookingTime = newDate;
          } else {
            this.nextAvailableDate(this.state.availableDays, newDate);
          }

          this.setState({ bookingTime: this.bookingTime.toDate(), timeSlots });
        }

        this.props.onDateTimeSelect(this.bookingTime.toDate(), ...args);
      }

      /*
       * @param {Array} dates available days of a partner.
       * @param {Object} date Moment|BookingTime object
       *
       * @example ['2018-11-03', ..]
       */
      getTimeSlots(partnerAvailability, date = this.bookingTime) {
        return partnerAvailability
          .map((ts) => ({ start: moment.utc(ts.start).toDate() }))
          .filter(
            (ts) => {
              let tsDate = getMomentWithTz(ts.start, this.props.region);

              if (this.props.constrainTimesWithinRange) {
                const minDate = tsDate
                  .clone()
                  .startOf("day")
                  .add(330, "minutes");
                const maxDate = tsDate
                  .clone()
                  .startOf("day")
                  .add(1410, "minutes");

                return (
                  tsDate.isSame(date.toDate(), "day") &&
                  tsDate.isSame(date.toDate(), "month") &&
                  tsDate.isSameOrAfter(minDate) &&
                  tsDate.isSameOrBefore(maxDate)
                );
              }

              return (
                tsDate.isSame(date.toDate(), "day") &&
                tsDate.isSame(date.toDate(), "month")
              );
            }

            // dates.month(ts.start) === dates.month(date.toDate())
          );
      }

      /**
       * Parses partner available date/ times in UTC format and reformats it to ISO 8601
       * @param startTimes
       * @returns {*}
       */
      getAvailableDays(startTimes) {
        return startTimes
          .map((val) => {
            var date = moment.utc(val.start);

            return {
              start: date.toISOString(),
              label: getMomentWithTz(date, this.props.region).format(
                "YYYY-MM-DD"
              ),
            };
          })
          .reduce((acc, val) => {
            if (!isDateInDayRange(val.start, this.props.region)) {
              return acc;
            }

            if (acc[val.label]) {
              acc[val.label].push(val);
            } else {
              acc[val.label] = [val];
            }

            return acc;
          }, {});
      }

      /*
       * Check if there's a time slot with this date/ time. Accepts already mapped partner availability

       * @example { '2023-03-28': { start: new Date(), label: 2023-03-28 }, .. }
       * @param Array dates mapped array with dates in ISO 8601 format
       * @param Moment date
       */
      isTimeAvailable(startTimes, date) {
        if (!startTimes || !date || !date.isValid()) {
          return;
        }

        var availableDates = Object.keys(startTimes);

        if (!availableDates || !availableDates.length) {
          return;
        }

        var day = getMomentWithTz(date, this.props.region).format("YYYY-MM-DD");

        return (
          startTimes[day] &&
          startTimes[day].some((ts) => date.toISOString() === ts.start)
        );
      }

      /*
       * Hash table with dates
       * Sets BookingTime's time
       * Expects dates in the following form. Used in UNSAFE_componentWillReceiveProps
       * If date is set then we went from onChange handler
       * Depends on filters!! from Day & Time Picker
       *
       * @example { '2020-06-17': { date: new Date(), label: 12 AM }, .. }
       * @param Array dates mapped array
       */

      nextAvailableDate(startTimes, date, addedTime = 3, skipSetState = false) {
        var availableDates, // list of all available dates
          matchedDaysOfWeek = [], // list of days that met a criteria
          matchedTimesOfDay = []; // list of times that met a criteria
        var now = getMomentWithTz(new Date(), this.props.region);
        var nextDate;
        var afterDate = date;

        if (!startTimes) {
          return;
        }

        if (!(date && date.isSameOrAfter(now))) {
          afterDate = now;
        }

        availableDates = Object.keys(startTimes);

        if (!availableDates || !availableDates.length) {
          return;
        }

        if (alignedToHalfAnHour) {
          roundToHalfAnHour(afterDate);
        }

        if (afterDate.isBefore(moment().add(addedTime, "hours"))) {
          afterDate.add(addedTime, "hours");
        }

        const daysOfWeek = []; // not used
        const timesOfDay = []; // not used

        if (daysOfWeek.length) {
          matchedDaysOfWeek = availableDates.filter((day) => {
            let date = moment(day, "YYYY-MM-DD");
            let weekDayName = date.format("ddd"); // Mon, Tue, etc.

            return (
              date.isSameOrAfter(afterDate) &&
              daysOfWeek.some((d) => d.name === weekDayName)
            );
          });
        }

        const pickMatchedTimeSlots = (slot) => {
          const date = moment(slot.start);
          const hour = date.hour();
          const minutes = date.minutes();

          // ???
          const _afterDate = afterDate.isSame(new Date(), "day")
            ? afterDate
            : afterDate.clone().startOf("day");

          return timesOfDay.some((tod) => {
            return (
              date.isSameOrAfter(_afterDate) &&
              ((tod.id === "morning" &&
                hour * 60 + minutes >= 330 &&
                hour <= 12) ||
                (tod.id === "midday" && hour >= 12 && hour <= 16) ||
                (tod.id === "evening" && hour >= 16 && hour <= 23))
            );
          });
        };

        const pickMatchedTimesOfDay = (day) => {
          let slots = startTimes[day];

          return slots.find(pickMatchedTimeSlots);
        };

        if (timesOfDay.length) {
          matchedTimesOfDay = availableDates.filter(pickMatchedTimesOfDay);
        }

        const intersectedDays = matchedDaysOfWeek.filter((value) =>
          matchedTimesOfDay.includes(value)
        );

        if (intersectedDays.length) {
          intersectedDays.some((day) => {
            const slots = startTimes[day];

            nextDate = slots.find(pickMatchedTimeSlots);

            return nextDate;
          });
        } else if (matchedDaysOfWeek.length) {
          matchedDaysOfWeek.some((day) => {
            const slots = startTimes[day];

            nextDate = slots.find((slot) => {
              return moment(slot.start).isSameOrAfter(afterDate) && slot.start;
            });

            return nextDate;
          });
        } else if (matchedTimesOfDay.length) {
          matchedTimesOfDay.some((day) => {
            const slots = startTimes[day];

            nextDate = slots.find(pickMatchedTimeSlots);

            return nextDate;
          });
        }

        // Fallback
        if (!nextDate || (date && !date.isSame(nextDate.start, "day"))) {
          afterDate = getMomentWithTz(afterDate, this.props.region).startOf(
            "day"
          );

          availableDates.some((day) => {
            const dayStartTimes = startTimes[day];

            nextDate = dayStartTimes.find((v) => {
              const dateMoment = moment(v.start);
              const nowMoment = getMomentWithTz(new Date(), this.props.region); //moment();

              return (
                dateMoment.isSameOrAfter(afterDate) &&
                (!dateMoment.isSame(nowMoment, "day") ||
                  dateMoment.isSameOrAfter(nowMoment)) &&
                v.start
              );
            });

            return nextDate;
          });

          // No dates available after afterDate
          // TODO dry with the code above
          if (!nextDate) {
            availableDates.some((day) => {
              const dayStartTimes = startTimes[day];

              nextDate = dayStartTimes.find((v) => {
                const dateMoment = moment(v.start);
                const nowMoment = getMomentWithTz(
                  new Date(),
                  this.props.region
                ); //moment();

                return (
                  (!dateMoment.isSame(nowMoment, "day") ||
                    dateMoment.isSameOrAfter(nowMoment)) &&
                  v.start
                );
              });
            });
          }
        }

        if (nextDate) {
          this.bookingTime = getMomentWithTz(nextDate.start, this.props.region);

          if (!skipSetState) {
            this.setState({ bookingTime: this.bookingTime.toDate() });
          }

          return this.bookingTime; //nextDate.start;
        }
      }
    }

    return hoistNonReactStatic(withRouter(HOCComponent), WrappedComponent);
  }

  return wrapComponent;
};

function alignedToHalfAnHour(date) {
  return (
    date.seconds() === 0 &&
    date.milliseconds() === 0 &&
    date.minutes() % 30 === 0
  );
}

/**
 * @param {object} date - Moment object to round to
 */
function roundToHalfAnHour(date) {
  if (!date) {
    console.warn("Date is not set! Cannot round to half an hour");

    return date;
  }

  // If there're no seconds or milliseconds, probably we use the aligned date (there's a chance after all)
  // Again, for mobiles. On desktop we set correct times beforehand :)
  if (alignedToHalfAnHour(date)) {
    return date;
  }

  const minutes = date.minutes(),
    hours = date.hours();

  var m,
    h = hours;

  // Align forward
  if (minutes < 30) {
    m = 30;
  } else {
    m = 0;
    h += 1;
  }

  date.milliseconds(0);
  date.seconds(0);
  date.minutes(m);
  date.hours(h);

  return date;
}
